import FormatNumber from 'common/components/FormatPercent/FormatNumber';
import PercentageSelector from 'common/components/PercentageSelector/PercentageSelector';
import Modal from 'common/components/Modal/Modal';
import SpinnerButton from 'common/components/SpinnerButton';
import SpinnerContainer from 'common/components/SpinnerContainer';
import { IchiJson } from 'common/models/ichiJson';
import { AppSyncToken } from 'common/models/token';
import { AppSyncUserInfo } from 'common/models/userInfo';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { UnstakeEvent } from 'xstate/unstakeEvent';
import {
  EventLiquidityPercentageChangeArgs,
  EventUpdateAccountInfoArgs,
  initialUnstakeContext,
  unstakeService,
} from 'xstate/unstakeMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import { RemoteTxEvent } from 'xstate/txMachine';
import { TokenUtils } from 'utils/tokenUtils';
import debounce from 'lodash/debounce';
import { useAccount } from 'wagmi';
import useSelectedChain from 'hooks/useSelectedChain';
import { StakeApi } from 'sdk/stakeApi';

type Props = {
  isShowing: boolean;
  toggle: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  refetchUserInfo: () => Promise<void>;
  userInfo: AppSyncUserInfo;
  ichi: AppSyncToken;
  xichi: AppSyncToken;
};

const initPercentage = 25;

const UnstakeModalContainer: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const { address: accountAddress } = useAccount();

  const [innerLiquidityPercentage, setInnerLiquidityPercentage] =
    React.useState(initPercentage);
  const [amount, setAmount] = React.useState<string>(
    initialUnstakeContext.amount
  );
  const [inputError, setInputError] = React.useState<string>();
  const [isLoadingData, setIsLoadingData] = React.useState<boolean>(
    initialUnstakeContext.isLoadingData
  );
  const [isUnstaking, setIsUnstaking] = React.useState<boolean>(
    initialUnstakeContext.isUnstaking
  );

  const refreshData = async () => {
    await props.refetchUserInfo();
  };
  const debouncedRefreshData = React.useMemo(
    () => debounce(refreshData, 750),
    []
  );

  React.useEffect(() => {
    const subscription = unstakeService.subscribe((state) => {
      if (state.context.amount !== amount) {
        setAmount(state.context.amount);
      }

      if (state.context.inputError != inputError) {
        setInputError(state.context.inputError);
      }

      if (state.context.isLoadingData !== isLoadingData) {
        setIsLoadingData(state.context.isLoadingData);
      }

      if (state.context.isUnstaking !== isUnstaking) {
        setIsUnstaking(state.context.isUnstaking);
      }
    });

    unstakeService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        void debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
    // Note that if isDepositModalOpen, isWithdrawModalOpen are not included in the array here
    // Then the function will not re-render
  }, [unstakeService, amount, inputError, isLoadingData, isUnstaking]);
  // TODO: Have to disable these until I figure out what's going on, something isn't right, I need to drive the modal state
  // by the machine not useModal I think
  // useKey('Escape', () => props.toggle());

  // useServiceLogger(unstakeService, '[unstakeMachine]');

  React.useEffect(() => {
    const args: EventLiquidityPercentageChangeArgs = {
      value: innerLiquidityPercentage,
    };
    unstakeService.send(
      UnstakeEvent.LIQUIDITY_PERCENTAGE_CHANGE,
      XStateUtils.createEventPayload(args)
    );
  }, [innerLiquidityPercentage]);

  const unstakingAmountUSD =
    TokenUtils.getIchiStats(props.ichi, props.xichi).xichiPrice *
    Number(amount);

  // When the API is set (with the contracts) then trigger this state
  React.useEffect(() => {
    if (StakeApi.Stake.getInstance() != null && accountAddress && chainId) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
      };
      unstakeService.send(
        UnstakeEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress]);

  const Data = useStaticQuery<IchiJson>(graphql`
    query {
      ichiJson {
        ICHI {
          stakeText
        }
      }
    }
  `);

  const isUnstakeButtonDisabled =
    innerLiquidityPercentage === 0 ||
    amount === '0' ||
    isUnstaking ||
    isLoadingData;

  // disabled={state.context.xIchiBalance.eq(0)}
  return (
    <Modal
      header={
        <div className="primary-text-color pt-2 font-medium text-2xl text-center">
          Unstake
        </div>
      }
      isShowing={props.isShowing}
      hide={props.toggle}
    >
      <div className="px-8 flex flex-col items-center">
        <div className="grid pb-[20px] w-full">
          <PercentageSelector
            value={innerLiquidityPercentage}
            onChange={setInnerLiquidityPercentage}
          />

          {inputError && (
            <div className="error-message mt-[20px] p-1 text-center">
              {inputError}
            </div>
          )}
        </div>
        {innerLiquidityPercentage > 0 && (
          <div className="flex flex-row justify-between items-center content-inner-bg-color p-[20px] w-full">
            <div className="primary-text-color text-lg">Unstaking: </div>
            <div className="flex flex-col items-end">
              <SpinnerContainer isLoading={false}>
                <FormatNumber
                  value={unstakingAmountUSD}
                  prefix="$"
                  isLoading={false}
                  useNumbro={true}
                  className="primary-text-color font-medium text-xl"
                />
                <FormatNumber
                  value={parseFloat(amount)}
                  isLoading={false}
                  useNumbro={true}
                  className="secondary-text-color font-medium text-sm text-gray-500"
                />
              </SpinnerContainer>
            </div>
          </div>
        )}
        <div className="primary-text-color font-medium text-xs py-[20px]">
          {Data.ichiJson.ICHI.stakeText}
        </div>
      </div>

      {/* Footer */}
      <div className="footer-bg-color h-24 p-[20px] flex items-center justify-evenly">
        <SpinnerButton
          className="primary-button-bg-color h-10 w-40 rounded-[10px]"
          isLoading={isUnstaking || isLoadingData}
          disabled={isUnstakeButtonDisabled}
          onClick={() => unstakeService.send(UnstakeEvent.UNSTAKE)}
          data-dd-action-name="Unstake ICHI"
        >
          Unstake
        </SpinnerButton>
      </div>
    </Modal>
  );
};

export default UnstakeModalContainer;
